import { useEffect, useMemo, useRef, useState } from 'react';
import { resolveDiStatusValue } from '../../utils/productSelectionsRow';

const useProductSelectionsDi = ({
  rows,
  fetchDiOrderItemsByReferenceId,
  onError,
}) => {
  const [diByReferenceId, setDiByReferenceId] = useState({});
  const [diErrorShown, setDiErrorShown] = useState(false);
  const diLoadedRefsRef = useRef(new Set());

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (typeof fetchDiOrderItemsByReferenceId !== 'function') return;
      const refs = Array.from(
        new Set(
          (rows || [])
            .map((r) => (r?.referenceId != null ? String(r.referenceId).trim() : ''))
            .filter((v) => v.length > 0)
        )
      );
      if (!refs.length) return;
      const missing = refs.filter((ref) => !diLoadedRefsRef.current.has(ref));
      if (!missing.length) return;
      try {
        const result = await fetchDiOrderItemsByReferenceId(missing);
        const byRef = result?.byReferenceId || {};
        missing.forEach((ref) => diLoadedRefsRef.current.add(ref));
        if (!mounted) return;
        setDiByReferenceId((prev) => ({ ...(prev || {}), ...(byRef || {}) }));
      } catch (error) {
        if (!mounted) return;
        if (!diErrorShown) {
          setDiErrorShown(true);
          onError?.(error);
        }
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [rows, fetchDiOrderItemsByReferenceId, diErrorShown, onError]);

  const diEnrichedRows = useMemo(() => {
    const map = diByReferenceId || {};
    return (rows || []).map((row) => {
      const ref = row?.referenceId != null ? String(row.referenceId).trim() : '';
      const di = ref ? map?.[ref] : null;
      const resolvedDiStatus = di ? resolveDiStatusValue(row, di) : '';

      const next = {
        ...row,
        ...(di || {}),
        diStatus: resolvedDiStatus || row?.diStatus || '',
        diStatusResolved: resolvedDiStatus || row?.diStatus || '',
      };

      return next;
    });
  }, [rows, diByReferenceId]);

  return { diEnrichedRows };
};

export default useProductSelectionsDi;
